import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@lysi-web/shared-types';

interface DashboardLayoutProps {
  children: React.ReactNode;
  role: 'admin' | 'doctor' | 'consultant';
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, role }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(user?.role !== UserRole.SUPER_ADMIN);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const drawerWidth = sidebarCollapsed ? 80 : 260;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', overflowX: 'hidden' }}>
      {/* Sidebar — hidden on mobile, visible on desktop */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Sidebar
          role={role}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleSidebarToggle}
          drawerWidth={drawerWidth}
        />
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top Bar */}
        <TopBar
          onMenuClick={handleDrawerToggle}
          onSidebarToggle={handleSidebarToggle}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Page Content — keyed by location for slide-in animation on navigation */}
        <Box
          key={location.key}
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3, md: 4 },
            paddingBottom: {
              xs: `calc(80px + env(safe-area-inset-bottom))`,
              md: 4,
            },
            animation: 'slideInUp 200ms ease-out',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Bottom Tab Navigation — mobile only */}
      <BottomNav role={role} />
    </Box>
  );
};
